import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useToggles } from '../context/TogglesContext';
import { useAccent } from '../context/AccentContext';
import { UserManagementPanel } from 'auth-sso-kit/client';
import type { UserManagementPanelLabels } from 'auth-sso-kit/client';
import { useT } from '../i18n';
import { Upload, Trash2, RefreshCw, Database } from 'lucide-react';

interface UserStats {
  id: number;
  linkCount: number;
  criticalityCount: number;
  vrioCount: number;
}

const ACCENT_PRESETS = [
  { key: 'indigo', color: '#1a1682' },
  { key: 'teal', color: '#0d7377' },
  { key: 'violet', color: '#7c5cbf' },
  { key: 'rose', color: '#c05580' },
  { key: 'slate', color: '#4a5568' },
];

export function AdminPage() {
  const { toggles, setToggle, refreshToggles } = useToggles();
  const { accent, setAccent, logoUrl, refreshSettings } = useAccent();
  const { t, lang } = useT();
  const [savingToggle, setSavingToggle] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<Record<number, UserStats>>({});
  const [savingAccent, setSavingAccent] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Dev-only: reset dev DB from prod
  const [canRefresh, setCanRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [resetText, setResetText] = useState('');

  const userLabels: UserManagementPanelLabels = {
    regModeTitle: t('users.regMode.title'),
    regModeOpen: t('users.regMode.open'),
    regModeAllowlist: t('users.regMode.allowlist'),
    regModeOpenHelp: t('users.regMode.openHelp'),
    regModeAllowlistHelp: t('users.regMode.allowlistHelp'),
    allowlistTitle: t('users.allowlist.title'),
    allowlistNotEnforced: t('users.allowlist.notEnforced'),
    allowlistHelpEnforced: t('users.allowlist.helpEnforced'),
    allowlistHelpNotEnforced: t('users.allowlist.helpNotEnforced'),
    emailPlaceholder: t('users.emailPlaceholder'),
    namePlaceholder: t('users.namePlaceholder'),
    addToAllowlist: t('users.addToAllowlist'),
    createAccount: t('users.createAccount'),
    errValidEmailRequired: t('users.errValidEmail'),
    colEmail: t('users.col.email'),
    colStatus: t('users.col.status'),
    colAdded: t('users.col.added'),
    statusRegistered: t('users.status.registered'),
    statusPending: t('users.status.pending'),
    impersonate: t('users.impersonate'),
    remove: t('users.remove'),
    confirmRemoveEmail: t('users.confirmRemoveEmail'),
    userMgmtTitle: t('users.mgmt.title'),
    colUser: t('users.col.user'),
    colRole: t('users.col.role'),
    colJoined: t('users.col.joined'),
    badgeAdmin: t('users.badge.admin'),
    badgeUser: t('users.badge.user'),
    removeAdmin: t('users.removeAdmin'),
    makeAdmin: t('users.makeAdmin'),
    rolesButton: t('users.roles'),
    resetPassword: t('users.resetPassword'),
    deleteButton: t('users.delete'),
    confirmDeleteUser: t('users.confirmDelete'),
    newPasswordPlaceholder: t('users.newPasswordPlaceholder'),
    setButton: t('users.set'),
    saveRoles: t('users.saveRoles'),
    errPasswordMin: t('users.errPasswordMin'),
    passwordResetDone: t('users.passwordResetDone'),
  };

  useEffect(() => {
    api.getUserStats().then((stats: any) => {
      const map: Record<number, UserStats> = {};
      (stats as UserStats[]).forEach(s => { map[s.id] = s; });
      setUserStats(map);
    }).catch(console.error);
    api.getSettings().then((s: any) => {
      setCanRefresh(s.canRefreshFromProd === 'true');
      if (s.dev_last_refreshed_from_prod) setLastRefreshed(s.dev_last_refreshed_from_prod);
    }).catch(() => {});
  }, []);

  const doRefreshFromProd = async () => {
    setRefreshing(true);
    try {
      const r = await api.refreshDevFromProd() as { refreshedAt: string };
      setLastRefreshed(r.refreshedAt);
      alert(t('admin.devReset.done'));
      window.location.reload();
    } catch (err: any) {
      alert(err.message || t('admin.devReset.failed'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleChange = async (key: string, enabled: boolean) => {
    setSavingToggle(key);
    try {
      await setToggle(key as any, enabled);
      await refreshToggles();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSavingToggle(null);
    }
  };

  const handleLogoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (!file.type.includes('png')) return alert(t('admin.logo.errPng'));
      if (file.size > 1.5 * 1024 * 1024) return alert(t('admin.logo.errSize'));

      setUploadingLogo(true);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64 = (reader.result as string).split(',')[1];
            await fetch('/api/admin/logo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ data: base64 }),
            });
            refreshSettings();
          } catch (err) {
            console.error(err);
          } finally {
            setUploadingLogo(false);
          }
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error(err);
        setUploadingLogo(false);
      }
    };
    input.click();
  };

  const handleLogoRemove = async () => {
    setUploadingLogo(true);
    try {
      await fetch('/api/admin/logo', {
        method: 'DELETE',
        credentials: 'include',
      });
      refreshSettings();
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div>
      {canRefresh && (
        <div className="card mb-16">
          <h3 className="card-title mb-12">
            <Database className="icon-sm" style={{ verticalAlign: -2, marginRight: 6 }} />
            {t('admin.devReset.title')}
          </h3>
          <p className="text-sm text-muted mb-12">
            {t('admin.devReset.help')}
          </p>
          {!confirming ? (
            <div className="flex gap-12 items-center">
              <button className="btn btn-accent btn-sm" disabled={refreshing} onClick={() => { setResetText(''); setConfirming(true); }}>
                <RefreshCw className="icon-sm" /> {t('admin.devReset.button')}
              </button>
              {lastRefreshed && <span className="text-sm text-muted">{t('admin.devReset.last')} {new Date(lastRefreshed).toLocaleString(lang === 'fr' ? 'fr-CH' : 'en-CH')}</span>}
            </div>
          ) : (
            <div className="flex flex-col gap-8" style={{ maxWidth: 380 }}>
              <p className="text-sm">{t('admin.devReset.confirmPrompt')}</p>
              <input className="form-input" value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" autoFocus />
              <div className="flex gap-8">
                <button className="btn btn-ghost btn-sm" disabled={refreshing} onClick={() => setConfirming(false)}>{t('common.cancel')}</button>
                <button className="btn btn-accent btn-sm" disabled={resetText !== 'RESET' || refreshing} onClick={doRefreshFromProd}>
                  {refreshing ? t('admin.devReset.resetting') : t('admin.devReset.confirm')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card mb-16">
        <h3 className="card-title mb-12">{t('admin.toggles.title')}</h3>
        <p className="text-sm text-muted mb-16">
          {t('admin.toggles.help')}
        </p>

        <div className="flex flex-col gap-12">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">{t('admin.toggles.linking.title')}</div>
              <div className="text-sm text-muted">{t('admin.toggles.linking.help')}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                className="toggle-input"
                checked={toggles.rcLinking}
                onChange={(e) => handleToggleChange('rcLinking', e.target.checked)}
                disabled={savingToggle === 'rcLinking'}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">{t('admin.toggles.linkQual.title')}</div>
              <div className="text-sm text-muted">{t('admin.toggles.linkQual.help')}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                className="toggle-input"
                checked={toggles.rcLinkQualification}
                onChange={(e) => handleToggleChange('rcLinkQualification', e.target.checked)}
                disabled={savingToggle === 'rcLinkQualification'}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">{t('admin.toggles.scoring.title')}</div>
              <div className="text-sm text-muted">{t('admin.toggles.scoring.help')}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                className="toggle-input"
                checked={toggles.rcScoring}
                onChange={(e) => handleToggleChange('rcScoring', e.target.checked)}
                disabled={savingToggle === 'rcScoring'}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">{t('admin.toggles.weightMatQual.title')}</div>
              <div className="text-sm text-muted">{t('admin.toggles.weightMatQual.help')}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                className="toggle-input"
                checked={toggles.rcWeightMaturityQualification}
                onChange={(e) => handleToggleChange('rcWeightMaturityQualification', e.target.checked)}
                disabled={savingToggle === 'rcWeightMaturityQualification'}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">{t('admin.toggles.vrio.title')}</div>
              <div className="text-sm text-muted">{t('admin.toggles.vrio.help')}</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                className="toggle-input"
                checked={toggles.rcVrio}
                onChange={(e) => handleToggleChange('rcVrio', e.target.checked)}
                disabled={savingToggle === 'rcVrio'}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="card mb-16">
        <h3 className="card-title mb-12">{t('admin.accent.title')}</h3>
        <p className="text-sm text-muted mb-12">
          {t('admin.accent.help')}
        </p>
        <div className="flex gap-8 items-center">
          {ACCENT_PRESETS.map((preset) => (
            <button
              key={preset.color}
              title={t(`admin.accent.color.${preset.key}`)}
              disabled={savingAccent}
              onClick={async () => {
                setSavingAccent(true);
                try { await setAccent(preset.color); } catch (e) { console.error(e); }
                finally { setSavingAccent(false); }
              }}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: preset.color,
                border: accent === preset.color ? '2px solid var(--ink-1)' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'border-color 0.1s, transform 0.1s',
              }}
            />
          ))}
          <input
            type="color"
            value={accent}
            disabled={savingAccent}
            onChange={async (e) => {
              setSavingAccent(true);
              try { await setAccent(e.target.value); } catch (err) { console.error(err); }
              finally { setSavingAccent(false); }
            }}
            style={{ width: 32, height: 32, padding: 2, border: '1px solid var(--line)', borderRadius: 'var(--r1)', cursor: 'pointer' }}
            title={t('admin.accent.custom')}
          />
        </div>
      </div>

      <div className="card mb-16">
        <h3 className="card-title mb-12">{t('admin.logo.title')}</h3>
        <p className="text-sm text-muted mb-12">
          {t('admin.logo.help')}
        </p>
        <div className="flex gap-12 items-center">
          {logoUrl ? (
            <>
              <div style={{
                border: '1px solid var(--line)',
                borderRadius: 'var(--r2)',
                padding: 'var(--s8)',
                background: 'var(--bg-inset)',
                display: 'inline-flex',
                alignItems: 'center',
              }}>
                <img src={logoUrl} alt={t('admin.logo.current')} style={{ height: 32 }} />
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleLogoUpload}
                disabled={uploadingLogo}
              >
                {t('admin.logo.replace')}
              </button>
              <button
                className="btn btn-ghost btn-sm text-err"
                onClick={handleLogoRemove}
                disabled={uploadingLogo}
              >
                <Trash2 className="icon-sm" /> {t('admin.logo.remove')}
              </button>
            </>
          ) : (
            <button
              className="btn btn-ghost"
              onClick={handleLogoUpload}
              disabled={uploadingLogo}
            >
              <Upload className="icon-sm" /> {t('admin.logo.upload')}
            </button>
          )}
        </div>
      </div>

      <UserManagementPanel
        labels={userLabels}
        extraColumns={[
          { header: t('admin.users.col.links'), render: (user) => userStats[user.id]?.linkCount ?? 0 },
          { header: t('admin.users.col.ratings'), render: (user) => userStats[user.id]?.criticalityCount ?? 0 },
          { header: t('admin.users.col.vrio'), render: (user) => userStats[user.id]?.vrioCount ?? 0 },
        ]}
      />
    </div>
  );
}
